import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import { useServiceUserWords } from '../../useServiceUserWords'
import { ReadGroup, ReadItem, ReadWide } from '../readLayout'
import {
  debriefRequiredKey,
  type InvestigationCase,
  recordedDebriefs,
  tickedFactorGroups
} from '../readRules'
import ContributingFactorsValue from './investigation/ContributingFactorsValue'
import DebriefReadSection from './investigation/DebriefReadSection'

const testId = 'incident-case-v2-view-investigation'

/**
 * Step 7, read only: whether a debrief was needed and the debriefs recorded,
 * then the root cause analysis and the lessons identified.
 */
const InvestigationReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { singular: serviceUser } = useServiceUserWords()
  const saved = (incidentReport.case ?? {}) as InvestigationCase
  const requiredKey = debriefRequiredKey(saved)
  const factorGroups = tickedFactorGroups(saved.contributingFactors ?? [])

  return (
    <>
      <ReadItem
        label={t(
          incidentReportsConstants.CASE_V2_VIEW_INVESTIGATION_DEBRIEF_REQUIRED
        )}
        value={requiredKey ? t(requiredKey) : null}
        testId={`${testId}-debrief-required`}
      />
      {recordedDebriefs(saved).map(({ debrief, kind }) => (
        <DebriefReadSection
          key={kind}
          kind={kind}
          debrief={debrief}
          testId={testId}
        />
      ))}
      <ReadGroup
        title={t(
          incidentReportsConstants.CASE_V2_INVESTIGATION_ROOT_CAUSE_ANALYSIS
        )}
        testId={`${testId}-root-cause-analysis`}
      >
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_CAUSE
            )}
            value={saved.immediateCause}
            testId={`${testId}-immediate-cause`}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_CONTRIBUTING_FACTORS
            )}
            value={
              factorGroups.length > 0 ? (
                <ContributingFactorsValue
                  groups={factorGroups}
                  testId={`${testId}-contributing-factors`}
                />
              ) : null
            }
            testId={`${testId}-contributing-factors`}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_EXPLANATION
            )}
            value={saved.contributingFactorsExplanation}
            testId={`${testId}-explanation`}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_SERVICE_USER_OPINION,
              { serviceUser }
            )}
            value={saved.serviceUserOpinion}
            testId={`${testId}-service-user-opinion`}
          />
        </ReadWide>
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_LESSONS_IDENTIFIED
            )}
            value={saved.lessonsIdentified}
            testId={`${testId}-lessons-identified`}
          />
        </ReadWide>
      </ReadGroup>
    </>
  )
}

export default InvestigationReadPanel
